import { createServerFn } from "@tanstack/react-start";

/**
 * Server-only Calendly lookup: exchanges a scheduled invitee URI for the
 * booker's contact details so a base Loops contact can be created the moment a
 * demo is booked.
 *
 * The token is read from `process.env.CALENDLY_TOKEN` inside the handler
 * (Cloudflare Workers inject env at request time — same pattern as
 * `loops.functions.ts`). Set it in the Cloudflare dashboard for production and
 * in `.dev.vars` for local dev.
 */

/**
 * The invitee URI arrives from an untrusted `postMessage` — any script or frame
 * on the page can forge one, so the only path a validated URI may point at is a
 * single Calendly invitee resource on the real API host.
 */
const INVITEE_PATH = /^\/scheduled_events\/[A-Za-z0-9_-]+\/invitees\/[A-Za-z0-9_-]+$/;

/** Reads the Calendly API token from the runtime environment. */
function getCalendlyApiToken(): string | undefined {
  const fromProcess = process.env.CALENDLY_TOKEN;
  return fromProcess ? fromProcess : undefined;
}

export type CalendlyInvitee = {
  email: string;
  firstName: string;
  lastName: string;
  /** Website from the booking form's custom question; null when not asked/answered. */
  website: string | null;
};

/** Matches the booking form's "website" custom question by its label. */
const WEBSITE_QUESTION = /website|site|url/i;

/**
 * Validates the untrusted invitee URI. Returns a parsed `URL` only when it is
 * an https link to `api.calendly.com` whose path is exactly an invitee
 * resource; anything else (an attacker-controlled host, a redirect target, an
 * unrelated endpoint) returns null so no request is ever made to it.
 */
function safeInviteeUrl(inviteeUri: string): URL | null {
  try {
    const url = new URL(inviteeUri);
    if (url.protocol !== "https:") return null;
    if (url.hostname !== "api.calendly.com") return null;
    if (!INVITEE_PATH.test(url.pathname)) return null;
    return url;
  } catch {
    return null;
  }
}

/**
 * Fetches an invitee's email/name/website from Calendly. Fails soft: an invalid
 * URI, a missing token, a non-200, a missing email, or a network error all
 * return null rather than throwing, and only the whitelisted fields ever leave
 * the server — the rest of the Calendly payload is dropped.
 */
export const getCalendlyInvitee = createServerFn({ method: "POST" })
  .inputValidator((data: { inviteeUri: string }) => data)
  .handler(async ({ data }): Promise<CalendlyInvitee | null> => {
    const url = safeInviteeUrl(data.inviteeUri);
    if (!url) return null;

    const token = getCalendlyApiToken();
    if (!token) return null;

    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return null;

      const body = (await res.json().catch(() => null)) as {
        resource?: {
          email?: string;
          first_name?: string | null;
          last_name?: string | null;
          name?: string | null;
          questions_and_answers?: { question?: string; answer?: string }[];
        };
      } | null;

      const resource = body?.resource;
      const email = resource?.email?.trim();
      if (!email) return null;

      // `first_name`/`last_name` can be null (single-field booking forms), so
      // fall back to splitting the combined `name` on its first space.
      const name = resource?.name?.trim() ?? "";
      const firstSpace = name.indexOf(" ");
      const nameFirst = firstSpace === -1 ? name : name.slice(0, firstSpace);
      const nameLast = firstSpace === -1 ? "" : name.slice(firstSpace + 1).trim();

      const firstName = (resource?.first_name ?? nameFirst).trim();
      const lastName = (resource?.last_name ?? nameLast).trim();

      const websiteAnswer = resource?.questions_and_answers?.find((qa) =>
        WEBSITE_QUESTION.test(qa.question ?? ""),
      )?.answer;
      const website = websiteAnswer?.trim() ? websiteAnswer.trim() : null;

      return { email, firstName, lastName, website };
    } catch {
      return null;
    }
  });
